// Global Instructions Rule Applied!
//
// Capital call notice sections of the funds Terms tab: the account capital
// calls are paid into, and the notice itself — its layout and wording.
// (What a notice prints ABOUT the fund — general partner, address, LPA
// sections, notice periods, sender — are fund terms, in TermsTab's own
// sections.)
//
// Fields auto-PATCH one at a time like every other Terms-tab field, to the
// two notice routes rather than PATCH /funds: the bank account and the
// settings live in their own tables (fund_bank_accounts,
// fund_notice_settings), created on the first save.
//
// A wording field shows the text that PRINTS: the fund's own wording where
// it has one, else the layout's default. Editing it stores an override;
// clearing it (or typing the default back) returns to the default.

import React, { useMemo } from 'react'
import { Alert, Col, Spin, Tag } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../../../core/components/InputManager/types.ts'
import { API_ROUTES } from '../../../../../core/utils/endpoints.ts'
import { useFundNoticeSettings } from '../../../hooks/useFundNoticeSettings.ts'
import {
  FundBankAccountRowFields,
  FundNoticeSettings,
  FundNoticeSettingsRowFields
} from '../../../../../state_manager/data_models/fund_notice_settings.ts'

// InputManager builds `${backend}/${patchEndpoint}`: the route path without
// its leading slash.
const SETTINGS_PATCH = API_ROUTES.FUNDS.NOTICE_SETTINGS_PATCH.path.replace(/^\//, '')
const BANK_PATCH = API_ROUTES.FUNDS.NOTICE_BANK_ACCOUNT_PATCH.path.replace(/^\//, '')

// A wording field's key: not a column (the blocks share one jsonb column),
// so its valueTransformer builds the PATCH body.
const WORDING_KEY_PREFIX = 'noticewording__'

interface NoticeSettingsSectionsProps {
  fundId: string
  /** One label-column width across the tab (TermsTab's maxLabelWidth). */
  maxLabelWidth: number
}

function bankText(key: string, label: string, value: string | null | undefined, labelTooltip?: string): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.TEXT,
    value: value ?? null,
    editable: true,
    patchEndpoint: BANK_PATCH,
    labelTooltip
  }
}

function bankAddress(key: string, label: string, value: string | null | undefined): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.TEXTAREA,
    value: value ?? null,
    editable: true,
    rows: 2,
    autoSize: { minRows: 2, maxRows: 6 },
    patchEndpoint: BANK_PATCH,
    labelTooltip: 'One line per row, as it prints.'
  }
}

export function getBankAccountFields(settings: FundNoticeSettings): FieldConfig[] {
  const bank = settings.bank_account
  return [
    bankText(FundBankAccountRowFields.fund_bank_accounts__bank_name, 'Bank', bank.fund_bank_accounts__bank_name),
    bankAddress(FundBankAccountRowFields.fund_bank_accounts__bank_address, 'Bank Address', bank.fund_bank_accounts__bank_address),
    bankText(FundBankAccountRowFields.fund_bank_accounts__bank_phone, 'Bank Phone', bank.fund_bank_accounts__bank_phone),
    bankText(FundBankAccountRowFields.fund_bank_accounts__aba_routing, 'ABA Routing Number', bank.fund_bank_accounts__aba_routing),
    bankText(FundBankAccountRowFields.fund_bank_accounts__account_number, 'Account Number', bank.fund_bank_accounts__account_number),
    bankText(FundBankAccountRowFields.fund_bank_accounts__swift_bic, 'SWIFT / BIC', bank.fund_bank_accounts__swift_bic),
    bankText(
      FundBankAccountRowFields.fund_bank_accounts__beneficiary_name,
      'Beneficiary',
      bank.fund_bank_accounts__beneficiary_name,
      "The account holder, as the bank has it. Empty prints the fund's legal name."
    ),
    bankAddress(
      FundBankAccountRowFields.fund_bank_accounts__beneficiary_address,
      'Beneficiary Address',
      bank.fund_bank_accounts__beneficiary_address
    )
  ]
}

export function getNoticeLayoutFields(settings: FundNoticeSettings): FieldConfig[] {
  return [
    {
      key: FundNoticeSettingsRowFields.fund_notice_settings__notice_template_id,
      label: 'Layout',
      type: FieldTypeEnum.SELECT,
      // the layout that APPLIES, so an unset fund shows the default it gets
      value: settings.prog__template_id ?? null,
      options: settings.templates.map((t) => ({ value: t.id, label: t.is_default ? `${t.label} (default)` : t.label })),
      selectType: SelectTypeEnum.FILTER,
      allowClear: true,
      editable: true,
      patchEndpoint: SETTINGS_PATCH,
      labelTooltip: (
        <div>
          {settings.templates.map((t) => (
            <div key={t.id} className='mb-1'>
              <strong>{t.label}</strong>
              {t.description ? ` — ${t.description}` : ''}
            </div>
          ))}
          <div>Clear the field to return to the default layout.</div>
        </div>
      )
    },
    {
      key: FundNoticeSettingsRowFields.fund_notice_settings__email_design_template_id,
      label: 'Notice Email Template',
      type: FieldTypeEnum.SELECT,
      // the template that APPLIES, so an unset fund shows the default it gets
      value:
        settings.settings.fund_notice_settings__email_design_template_id ??
        settings.prog__default_email_template_id ??
        null,
      options: settings.email_templates.map((t) => ({
        value: t.id,
        label: t.id === settings.prog__default_email_template_id ? `${t.name} (default)` : t.name
      })),
      selectType: SelectTypeEnum.FILTER,
      allowClear: true,
      editable: true,
      patchEndpoint: SETTINGS_PATCH,
      labelTooltip:
        'The email template "Send notices" on a capital call drafts the notice email from. Templates are edited under Email, Templates. Clear the field to return to the default template.'
    }
  ]
}

export function getNoticeWordingFields(settings: FundNoticeSettings): FieldConfig[] {
  const template = settings.templates.find((t) => t.id === settings.prog__template_id)
  const defaults = template?.text_blocks ?? {}
  const overrides = settings.settings.fund_notice_settings__text_overrides ?? {}

  const mergeFieldHelp = (help?: string | null) => (
    <div>
      {help ? <div className='mb-2'>{help}</div> : null}
      <div className='mb-1'>Merge fields — written in braces, filled in per investor; **text** prints bold:</div>
      {settings.merge_fields.map((f) => (
        <div key={f.key}>
          <code>{`{${f.key}}`}</code> {f.description}
        </div>
      ))}
    </div>
  )

  return settings.text_blocks.map((block): FieldConfig => {
    const fallback = (defaults[block.key] ?? '') || ''
    const own = (overrides[block.key] ?? '') || ''
    const customised = own.trim() !== ''
    return {
      key: `${WORDING_KEY_PREFIX}${block.key}`,
      label: block.label,
      type: FieldTypeEnum.TEXTAREA,
      value: customised ? own : fallback,
      displayValue: customised ? (
        <span>
          {own} <Tag color='purple'>This fund</Tag>
        </span>
      ) : undefined,
      editable: true,
      rows: 2,
      autoSize: { minRows: 2, maxRows: 8 },
      patchEndpoint: SETTINGS_PATCH,
      labelTooltip: mergeFieldHelp(block.help),
      // The blocks share one jsonb column: send the whole map with this
      // block replaced. Text equal to the default is not an override.
      valueTransformer: (value: any, record: any) => {
        const text = typeof value === 'string' ? value : ''
        const current = (record?.[FundNoticeSettingsRowFields.fund_notice_settings__text_overrides] ?? {}) as Record<
          string,
          string | null
        >
        return {
          text_overrides: { ...current, [block.key]: text.trim() === fallback.trim() ? '' : text }
        }
      }
    }
  })
}

export default function NoticeSettingsSections({ fundId, maxLabelWidth }: NoticeSettingsSectionsProps) {
  const { settings, loading, error, refetch } = useFundNoticeSettings(fundId)

  const bankFields = useMemo(() => (settings ? getBankAccountFields(settings) : []), [settings])
  const layoutFields = useMemo(() => (settings ? getNoticeLayoutFields(settings) : []), [settings])
  const wordingFields = useMemo(() => (settings ? getNoticeWordingFields(settings) : []), [settings])

  if (!settings) {
    return (
      <Col xs={24}>
        <FormSection title='Capital Call Notice' titleColor='purple'>
          {loading ? (
            <div className='flex justify-center py-6'>
              <Spin />
            </div>
          ) : (
            <Alert type='error' showIcon message='Failed to load the notice settings' description={error} />
          )}
        </FormSection>
      </Col>
    )
  }

  const inputManagerProps = {
    inputMode: 'read' as const,
    labelPosition: 'inline' as const,
    enableAutoPatch: true,
    // both PATCH routes are keyed by the fund
    patchId: fundId,
    recordId: fundId,
    // The saved value, the layout that applies and the default each wording
    // field falls back to are all resolved server-side.
    onPatchSuccess: () => refetch(),
    fieldWrapping: true,
    maxLabelWidth
  }

  return (
    <>
      <Col xs={24} xl={12}>
        <FormSection title='Capital Call Bank Account' titleColor='purple'>
          <InputManager
            {...inputManagerProps}
            currentRecord={settings.bank_account as Record<string, any>}
            fields={bankFields}
          />
        </FormSection>
      </Col>
      <Col xs={24} xl={12}>
        <FormSection title='Capital Call Notice' titleColor='purple'>
          <InputManager
            {...inputManagerProps}
            currentRecord={settings.settings as Record<string, any>}
            fields={layoutFields}
          />
          <div className='mt-2 pt-2 border-t border-gray-100'>
            <InputManager
              {...inputManagerProps}
              currentRecord={settings.settings as Record<string, any>}
              fields={wordingFields}
            />
          </div>
        </FormSection>
      </Col>
    </>
  )
}
